/**
 * Community Garden — Points Store (Centralized State Management)
 * 
 * GARDEN-134: Single source of truth for points state.
 * Replaces scattered points mutations across the renderer and UI.
 * GARDEN-138: Optimistic update support with rollback capability.
 */

export type PointsSubscriber = (points: number, previousPoints: number) => void;
export type PointsOperationType = 'add' | 'spend' | 'set';

// GARDEN-138: Optimistic operation status type
// Defined locally to keep the points store independent from UI orchestration.
export type OptimisticStatus = 'pending' | 'confirmed' | 'rolledBack';

const STORAGE_KEY = 'garden:points:v1';

class PointsStore {
  private points: number = 0;
  private subscribers: Set<PointsSubscriber> = new Set();
  private isInitialized: boolean = false;
  private pendingOptimisticOperations = new Map<string, number>(); // operationId -> expectedPoints
  /**
   * Guild scope for the cached value. Points are guild-scoped server-side,
   * but the localStorage fallback used a single global key, so a cached
   * value from one garden could surface in another (observed: stale "7346"
   * shown after a WS rejection in a different guild). Scope the cache key
   * by guild and never trust an unscoped value.
   */
  private guildId: string | null = null;

  constructor() {
    // Attempt to load from localStorage on initialization (fallback only)
    this.loadFromStorage();
  }

  /**
   * Set the guild context for points. Reloads the guild-scoped cached value
   * (if any) and notifies subscribers. Call when entering a garden.
   */
  setGuildContext(guildId: string | null): void {
    if (this.guildId === guildId) return;
    this.guildId = guildId;
    const previousPoints = this.points;
    this.points = 0;
    this.isInitialized = false;
    this.loadFromStorage();
    if (this.points !== previousPoints) {
      this.subscribers.forEach(callback => {
        try {
          callback(this.points, previousPoints);
        } catch (error) {
          console.error('[PointsStore] Subscriber error:', error);
        }
      });
      this.dispatchPointsChangeEvent(this.points, previousPoints);
    }
  }

  private storageKey(): string {
    return this.guildId ? `${STORAGE_KEY}:${this.guildId}` : STORAGE_KEY;
  }

  /**
   * GARDEN-138: Apply optimistic points update
   * Updates UI immediately before API confirmation
   */
  applyOptimisticUpdate(
    operationId: string,
    delta: number,
    operationType: PointsOperationType = 'add',
    onStatusChange?: (status: OptimisticStatus) => void
  ): void {
    const previousPoints = this.points;
    let expectedPoints: number;

    switch (operationType) {
      case 'add':
        expectedPoints = this.points + delta;
        break;
      case 'spend':
        expectedPoints = Math.max(0, this.points - delta);
        break;
      case 'set':
        expectedPoints = delta;
        break;
      default:
        expectedPoints = this.points + delta;
    }

    // Track the operation
    this.pendingOptimisticOperations.set(operationId, expectedPoints);

    // Apply optimistic update
    this.points = expectedPoints;

    // Notify subscribers
    this.subscribers.forEach(callback => {
      try {
        callback(this.points, previousPoints);
      } catch (error) {
        console.error('[PointsStore] Subscriber error:', error);
      }
    });

    // Dispatch custom event
    this.dispatchPointsChangeEvent(this.points, previousPoints);

    // If status change callback provided, store it for later
    if (onStatusChange) {
      // Store callback for this operation (implementation depends on integration pattern)
      const key = `_callback_${operationId}`;
      (this as unknown as Record<string, unknown>)[key] = onStatusChange;
    }
  }

  /**
   * GARDEN-138: Handle status update from optimistic update manager
   * Called by the optimistic update manager when operation status changes
   */
  handleOptimisticStatusUpdate(operationId: string, status: OptimisticStatus, previousPoints?: number): void {
    if (status === 'confirmed') {
      this.pendingOptimisticOperations.delete(operationId);
      // Clean up any stored callback
      const key = `_callback_${operationId}`;
      delete (this as unknown as Record<string, unknown>)[key];
    } else if (status === 'rolledBack') {
      this.pendingOptimisticOperations.delete(operationId);
      if (previousPoints !== undefined) {
        this.rollbackOptimisticUpdate(previousPoints, operationId);
      }
      const key = `_callback_${operationId}`;
      delete (this as unknown as Record<string, unknown>)[key];
    }
  }

  /**
   * GARDEN-138: Confirm an optimistic points operation
   * Call this when the API confirms the operation succeeded
   */
  confirmOptimisticUpdate(operationId: string): void {
    this.pendingOptimisticOperations.delete(operationId);
  }

  /**
   * GARDEN-138: Rollback an optimistic points operation
   */
  rollbackOptimisticUpdate(revertToPoints: number, operationId: string): void {
    const previousPoints = this.points;
    this.points = revertToPoints;
    this.pendingOptimisticOperations.delete(operationId);

    // Notify subscribers
    this.subscribers.forEach(callback => {
      try {
        callback(this.points, previousPoints);
      } catch (error) {
        console.error('[PointsStore] Subscriber error:', error);
      }
    });

    // Dispatch custom event
    this.dispatchPointsChangeEvent(this.points, previousPoints);
  }

  /**
   * GARDEN-138: Get current optimistic points (including pending operations)
   */
  getOptimisticPoints(): number {
    // Return the most recent optimistic value if there are pending operations
    let latestValue = this.points;
    for (const expectedPoints of this.pendingOptimisticOperations.values()) {
      latestValue = expectedPoints;
    }
    return latestValue;
  }

  /**
   * GARDEN-138: Check if there are pending points operations
   */
  hasPendingOperations(): boolean {
    return this.pendingOptimisticOperations.size > 0;
  }

  /**
   * Get current points value
   */
  getPoints(): number {
    return this.points;
  }

  /**
   * Set points value and notify all subscribers
   */
  setPoints(newPoints: number): void {
    const previousPoints = this.points;
    if (newPoints === previousPoints) return;

    this.points = newPoints;
    this.isInitialized = true;

    // Persist to localStorage as fallback
    this.saveToStorage();

    // Notify all subscribers
    this.subscribers.forEach(callback => {
      try {
        callback(newPoints, previousPoints);
      } catch (error) {
        console.error('[PointsStore] Subscriber error:', error);
      }
    });

    // Dispatch custom event for cross-component updates
    this.dispatchPointsChangeEvent(newPoints, previousPoints);
  }

  /**
   * Add points to current value
   */
  addPoints(delta: number): void {
    this.setPoints(this.points + delta);
  }

  /**
   * Subscribe to points changes. Returns unsubscribe function.
   */
  subscribe(callback: PointsSubscriber): () => void {
    this.subscribers.add(callback);
    
    // Immediately call with current value if already initialized
    if (this.isInitialized) {
      callback(this.points, this.points);
    }

    // Return unsubscribe function
    return () => {
      this.subscribers.delete(callback);
    };
  }

  /**
   * Check if store has been initialized with a value
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Reset points to zero (useful for logout/guild switch)
   */
  reset(): void {
    this.setPoints(0);
    this.isInitialized = false;
    try {
      localStorage.removeItem(this.storageKey());
      localStorage.removeItem(STORAGE_KEY); // legacy unscoped key
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Load points from localStorage (fallback only)
   */
  private loadFromStorage(): void {
    try {
      // Never load the legacy unscoped key into a guild context — it may
      // hold another guild's points. Clean it up instead.
      if (this.guildId) {
        localStorage.removeItem(STORAGE_KEY);
      }
      const stored = localStorage.getItem(this.storageKey());
      if (stored !== null) {
        const parsed = parseFloat(stored);
        if (!isNaN(parsed)) {
          this.points = parsed;
          this.isInitialized = true;
        }
      }
    } catch {
      // Ignore storage errors (e.g., private browsing mode)
    }
  }

  /**
   * Save points to localStorage (fallback only)
   */
  private saveToStorage(): void {
    try {
      localStorage.setItem(this.storageKey(), String(this.points));
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Dispatch custom event for components that prefer event-based updates
   */
  private dispatchPointsChangeEvent(newPoints: number, previousPoints: number): void {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('garden:points:change', {
        detail: { points: newPoints, previousPoints, delta: newPoints - previousPoints }
      }));
    }
  }
}

// Export singleton instance
export const pointsStore = new PointsStore();

// Export class for testing or custom instances
export { PointsStore };

/**
 * GARDEN-141: Centralized points formatting function
 * Ensures consistent display across all UI components (HUD, Profile, Leaderboard)
 * 
 * Rules:
 * - Whole numbers: display as-is (e.g., "150")
 * - 1 decimal place: display with 1 decimal (e.g., "150.5")
 * - 2+ decimal places: display with 2 decimals (e.g., "150.55")
 * - Never show "0.0" for non-zero values
 */
export function formatPoints(points: number): string {
  if (points === 0) return '0';
  if (Number.isInteger(points)) return String(points);
  
  const oneDecimal = points.toFixed(1);
  // If 1 decimal rounds to 0.0 but value is non-zero, show 2 decimals
  if (oneDecimal === '0.0') {
    return points.toFixed(2);
  }
  return oneDecimal;
}
